import React, { useState, useEffect, useRef, useCallback } from 'react';
import { avisar } from '../components/confirmacao';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import './FeedbacksAdmin.css';

const FeedbacksAdmin = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [playingAudios, setPlayingAudios] = useState({});
    const [loadingAudios, setLoadingAudios] = useState({});
    const [responses, setResponses] = useState({});
    const [savingResponses, setSavingResponses] = useState({});
    const [savedResponses, setSavedResponses] = useState({}); // Controla quais respostas foram salvas
    const [filterType, setFilterType] = useState('todos');
    const audioRefs = useRef({});
    const highlightedRowRef = useRef(null);

    // Estado do usuário (role e dados para transcrição)
    const [userRole, setUserRole] = useState(null);
    const [userId, setUserId] = useState(null);

    // Estado do modal de transcrição
    const [transcriptionModal, setTranscriptionModal] = useState({ open: false, feedbackId: null });
    const [transcriptionData, setTranscriptionData] = useState(null);
    const [loadingTranscription, setLoadingTranscription] = useState(false);
    const [copiedField, setCopiedField] = useState(null);

    // Estado do player de áudio
    const [audioPlayer, setAudioPlayer] = useState({ feedbackId: null, redacted: false, url: null, error: null, loading: false });
    // Admin limitado (core.admin_companies): só transcrição com contexto anonimizado e só áudio
    // anonimizado. Default restrito até a API responder.
    const [restrictedAdmin, setRestrictedAdmin] = useState(true);
    const feedbackAudioRef = useRef(null);

    // Parâmetros da URL
    const salesman = searchParams.get('salesman');
    const storeName = searchParams.get('store');
    const fullname = searchParams.get('fullname');
    const bucketName = searchParams.get('bucket_name');
    const feedbackIdParam = searchParams.get('feedback_id'); // legado (id único)
    const feedbackIdsParam = searchParams.get('feedback_ids'); // lista separada por vírgula
    const feedbackIdSet = feedbackIdsParam
        ? new Set(feedbackIdsParam.split(',').map(id => id.trim()))
        : feedbackIdParam
            ? new Set([String(feedbackIdParam)])
            : null;

    const loadFeedbacks = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Calcular datas - hoje e 60 dias atrás
            const today = new Date();
            const endDate = today.toISOString().split('T')[0]; // YYYY-MM-DD

            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 60);
            const startDateStr = startDate.toISOString().split('T')[0]; // YYYY-MM-DD

            // Obter user_id do usuário autenticado
            const userInfo = await apiService.getUserInfo();
            const fetchedUserId = userInfo.user.user_id;
            setUserId(fetchedUserId);
            setUserRole(userInfo.user.role || null);

            const requestBody = {
                user_id: fetchedUserId,
                salesman: salesman,
                start: startDateStr,
                end: endDate,
                store_name: storeName,
                show_all: true
            };

            // Se temos feedback_ids (vindos de alertas), enviar para a API para busca direta
            if (feedbackIdsParam) {
                requestBody.feedback_ids = [...new Set(feedbackIdsParam.split(',').map(id => id.trim()))].map(Number);
            }
            if (bucketName) {
                requestBody.bucket_name = bucketName;
            }

            const response = await apiService.getFeedbacks(requestBody);

            if (response.success) {
                setFeedbacks(response.data.feedbacks || []);

                // Carregar respostas existentes do campo comments_answer
                const existingResponses = {};
                const existingSavedResponses = {};
                (response.data.feedbacks || []).forEach(feedback => {
                    if (feedback.comments_answer && feedback.comments_answer.trim() !== '') {
                        existingResponses[feedback.id] = feedback.comments_answer;
                        existingSavedResponses[feedback.id] = true;
                    }
                });
                setResponses(existingResponses);
                setSavedResponses(existingSavedResponses);
            } else {
                setError(response.message || 'Erro ao carregar feedbacks');
            }

        } catch (error) {
            console.error('❌ Erro ao carregar feedbacks:', error);
            setError('Erro ao carregar feedbacks: ' + (error.message || 'Erro desconhecido'));
        } finally {
            setLoading(false);
        }
    }, [salesman, storeName, feedbackIdsParam, bucketName]);

    // Scroll para o primeiro feedback destacado quando os dados carregarem
    useEffect(() => {
        if (!loading && feedbackIdSet && highlightedRowRef.current) {
            highlightedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [loading, feedbacks]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!salesman || !storeName) {
            setError('Parâmetros salesman e store são obrigatórios');
            setLoading(false);
            return;
        }

        loadFeedbacks();

        // Cleanup function para pausar todos os áudios quando o componente for desmontado
        const currentAudioRefs = audioRefs.current;
        return () => {
            Object.values(currentAudioRefs).forEach(audio => {
                if (audio && !audio.paused) {
                    audio.pause();
                }
            });
        };
    }, [salesman, storeName, loadFeedbacks]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Data inválida';
        }
    };

    // O feedback vem da API como string (formato antigo) OU objeto {good, bad} (formato novo do n8n).
    // Renderizar um objeto direto como filho React quebra a tela inteira ("Objects are not valid as a
    // React child"). Este helper trata os dois casos.
    const renderFeedbackText = (fb) => {
        if (fb && typeof fb === 'object' && !Array.isArray(fb)) {
            const { good, bad } = fb;
            if (good == null && bad == null) return JSON.stringify(fb);
            return (
                <>
                    {good && (
                        <div className="feedback-good">
                            <strong>✅ Pontos positivos</strong>
                            <div style={{ whiteSpace: 'pre-line' }}>{good}</div>
                        </div>
                    )}
                    {bad && (
                        <div className="feedback-bad" style={{ marginTop: good ? 8 : 0 }}>
                            <strong>⚠️ Pontos de atenção</strong>
                            <div style={{ whiteSpace: 'pre-line' }}>{bad}</div>
                        </div>
                    )}
                </>
            );
        }
        return fb;
    };

    const formatAudioLength = (seconds) => {
        if (!seconds || seconds <= 0) return 'N/A';

        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const getReadIcon = (read) => {
        if (read === 1) {
            return <span className="icon read" title="Lido">✅</span>;
        } else {
            return <span className="icon unread" title="Não lido">🙈</span>;
        }
    };

    const getLikesIcon = (likes) => {
        if (likes === null || likes === undefined) {
            return <span className="icon no-reaction" title="Sem reação">-</span>;
        } else if (likes === 1) {
            return <span className="icon thumbs-up" title="Curtiu">👍</span>;
        } else {
            return <span className="icon thumbs-down" title="Não curtiu">👎</span>;
        }
    };

    const handleBackClick = () => {
        const scrollY = searchParams.get('scrollY');
        if (scrollY) {
            navigate(`/?scrollY=${scrollY}`);
        } else {
            navigate(-1);
        }
    };

    // Abrir modal de transcrição
    const handleOpenTranscription = async (feedbackId, feedbackBucketName) => {
        setTranscriptionModal({ open: true, feedbackId });
        setTranscriptionData(null);
        setLoadingTranscription(true);
        setCopiedField(null);

        try {
            const result = await apiService.getTranscription(userId, feedbackBucketName, feedbackId);
            if (result.success) {
                setTranscriptionData(result.data);
            } else {
                setTranscriptionData({ error: result.message || 'Erro ao buscar transcrição' });
            }
        } catch (err) {
            setTranscriptionData({ error: err.message || 'Erro ao buscar transcrição' });
        } finally {
            setLoadingTranscription(false);
        }
    };

    // Verificar áudio do feedback
    const handleListenAudio = async (feedbackId, feedbackBucketName, redacted = false) => {
        // Parar só se for o MESMO botão (original e anonimizado são independentes)
        if (audioPlayer.feedbackId === feedbackId && audioPlayer.redacted === redacted && audioPlayer.url) {
            setAudioPlayer({ feedbackId: null, redacted: false, url: null, error: null, loading: false });
            return;
        }

        setAudioPlayer({ feedbackId, redacted, url: null, error: null, loading: true });

        try {
            const result = await apiService.listenAudioFeedback(feedbackId, userId, feedbackBucketName, redacted);
            if (result.success) {
                setAudioPlayer({ feedbackId, redacted, url: result.data.presigned_url, error: null, loading: false });
            } else if (result.notFound) {
                setAudioPlayer({ feedbackId, redacted, url: null, error: result.message, loading: false });
            } else {
                setAudioPlayer({ feedbackId, redacted, url: null, error: result.message || 'Erro ao buscar áudio', loading: false });
            }
        } catch (err) {
            setAudioPlayer({ feedbackId, redacted, url: null, error: err.message || 'Erro ao buscar áudio', loading: false });
        }
    };

    // Escopo do admin — a API é quem decide; isto só evita oferecer o que ela vai negar.
    useEffect(() => {
        (async () => {
            const r = await apiService.adminScope();
            setRestrictedAdmin(!!r.data?.restricted);
        })();
    }, []);

    // Tocar áudio quando a URL pré-assinada chegar
    useEffect(() => {
        if (audioPlayer.url && feedbackAudioRef.current) {
            feedbackAudioRef.current.load();
            feedbackAudioRef.current.play().catch(() => { /* browser may block */ });
        }
    }, [audioPlayer.url]);

    // Fechar modal de transcrição
    const handleCloseTranscription = () => {
        setTranscriptionModal({ open: false, feedbackId: null });
        setTranscriptionData(null);
        setCopiedField(null);
    };

    // Copiar texto para clipboard
    const handleCopyText = async (text, field) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback para contextos sem HTTPS
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopiedField(field);
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
            console.error('Erro ao copiar:', err);
        }
    };

    const handleResponseChange = (feedbackId, value) => {
        setResponses(prev => ({ ...prev, [feedbackId]: value }));
    };

    const handleSaveResponse = async (feedback) => {
        const feedbackId = feedback.id;
        const responseText = responses[feedbackId];
        if (!responseText || responseText.trim() === '') {
            return;
        }

        try {
            setSavingResponses(prev => ({ ...prev, [feedbackId]: true }));

            const result = await apiService.saveFeedbackResponse(
                feedbackId,
                responseText,
                feedback.user_id,
                feedback.store_id
            );

            if (result.success) {
                setSavedResponses(prev => ({ ...prev, [feedbackId]: true }));
            } else {
                throw new Error(result.message || 'Erro ao salvar resposta');
            }

        } catch (error) {
            console.error('❌ Erro ao salvar resposta:', error);
            avisar('Erro ao salvar resposta: ' + error.message, { titulo: 'Erro ao salvar' });
        } finally {
            setSavingResponses(prev => ({ ...prev, [feedbackId]: false }));
        }
    };

    // Função para filtrar feedbacks
    const getFilteredFeedbacks = () => {
        // Filtro por feedback_ids (prioridade máxima — vem da URL)
        if (feedbackIdSet) {
            return feedbacks.filter(feedback => feedbackIdSet.has(String(feedback.id)));
        }
        if (filterType === 'pendentes') {
            return feedbacks.filter(feedback => {
                const hasComment = feedback.comments && feedback.comments.trim() !== '' && feedback.comments !== '-';
                const isDislike = Number(feedback.likes) === -1;
                const hasResponse = savedResponses[feedback.id]; // Verifica se foi salva, não se foi digitada
                return (hasComment || isDislike) && !hasResponse;
            });
        }
        return feedbacks;
    };

    const handleAudioPlay = async (audioCommentId, feedbackDate, bucketName) => {
        try {
            // Extrair apenas o ID numérico se vier no formato "data__id"
            const actualId = audioCommentId.toString().includes('__')
                ? audioCommentId.toString().split('__').pop()
                : audioCommentId;

            setLoadingAudios(prev => ({ ...prev, [audioCommentId]: true }));

            // Se já está tocando, pausar
            if (playingAudios[audioCommentId]) {
                const audio = audioRefs.current[audioCommentId];
                if (audio) {
                    audio.pause();
                    setPlayingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                }
                return;
            }

            // Pausar todos os outros áudios
            Object.keys(audioRefs.current).forEach(id => {
                const audio = audioRefs.current[id];
                if (audio && !audio.paused) {
                    audio.pause();
                    setPlayingAudios(prev => ({ ...prev, [id]: false }));
                }
            });

            // Criar ou reutilizar elemento de áudio
            let audio = audioRefs.current[audioCommentId];
            if (!audio) {
                audio = new Audio();
                audioRefs.current[audioCommentId] = audio;

                // Event listeners
                audio.addEventListener('ended', () => {
                    setPlayingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                });

                audio.addEventListener('error', (e) => {
                    console.error('❌ Erro no áudio:', e);
                    setPlayingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                    setLoadingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                });

                audio.addEventListener('loadstart', () => {
                });

                audio.addEventListener('canplay', () => {
                    setLoadingAudios(prev => ({ ...prev, [audioCommentId]: false }));
                });
            }

            // Usar URL do proxy em vez da URL direta do S3, passando bucket_name do feedback
            const proxyUrl = `/api/audio-proxy/${actualId}${bucketName ? `?bucket_name=${encodeURIComponent(bucketName)}` : ''}`;

            audio.src = proxyUrl;
            await audio.play();
            setPlayingAudios(prev => ({ ...prev, [audioCommentId]: true }));

        } catch (error) {
            console.error('❌ Erro ao reproduzir áudio:', error);
            setError('Erro ao reproduzir áudio: ' + error.message);
        } finally {
            setLoadingAudios(prev => ({ ...prev, [audioCommentId]: false }));
        }
    };

    const renderAudioComments = (audioCommentIds, feedbackDate, bucketName) => {
        if (!audioCommentIds || audioCommentIds.length === 0) {
            return <span>-</span>;
        }

        return (
            <div className="audio-comments-list">
                {audioCommentIds.map((audioId, index) => {
                    // Extrair apenas o ID numérico para exibição
                    const displayId = audioId.toString().includes('__')
                        ? audioId.toString().split('__').pop()
                        : audioId;

                    // Extrair data se vier no formato completo, senão usar feedbackDate
                    const displayDate = audioId.toString().includes('__')
                        ? audioId.toString().split('__')[0]
                        : feedbackDate;

                    return (
                        <div key={audioId} className="audio-comment-item">
                            <span className="audio-id">#{displayId}</span>
                            <span className="audio-date">{displayDate}</span>
                            <button
                                className={`audio-play-button ${playingAudios[audioId] ? 'playing' : ''}`}
                                onClick={() => handleAudioPlay(audioId, feedbackDate, bucketName)}
                                disabled={loadingAudios[audioId]}
                                title={playingAudios[audioId] ? 'Pausar áudio' : 'Reproduzir áudio'}
                            >
                                {loadingAudios[audioId] ? (
                                    <span className="loading-spinner">⏳</span>
                                ) : playingAudios[audioId] ? (
                                    <span className="pause-icon">⏸️</span>
                                ) : (
                                    <span className="play-icon">▶️</span>
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) {
        return <Loading message="Carregando feedbacks..." fullScreen />;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Erro</h2>
                <p>{error}</p>
                <button onClick={handleBackClick} className="back-button">
                    Voltar
                </button>
            </div>
        );
    }

    return (
        <div className="feedbacks-admin">
            <div className="feedbacks-header">
                <button onClick={handleBackClick} className="back-button">
                    ← Voltar
                </button>
                <div className="salesman-info">
                    <h1>
                        Feedbacks{' '}
                        <span className="salesman-highlight">{fullname || salesman}</span>
                    </h1>
                    <p className="store-info">Loja: {storeName.replace(/_/g, ' ')}</p>
                    <p className="total-info">Total de feedbacks: {getFilteredFeedbacks().length} {feedbackIdSet ? `(filtrado por ${feedbackIdSet.size} ID${feedbackIdSet.size !== 1 ? 's' : ''})` : filterType === 'pendentes' ? '(pendentes de resposta)' : '(últimos 60 dias)'}</p>
                </div>
                <div className="filter-section">
                    {feedbackIdSet ? (
                        <button
                            className="filter-clear-button"
                            onClick={() => navigate(`/feedbacks?salesman=${encodeURIComponent(salesman)}&store=${encodeURIComponent(storeName)}`)}
                        >
                            ✕ Remover filtro de ID
                        </button>
                    ) : (
                        <>
                            <label className="filter-label">Filtrar por:</label>
                            <select
                                className="filter-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="todos">Todos</option>
                                <option value="pendentes">Pendentes de resposta</option>
                            </select>
                        </>
                    )}
                </div>
            </div>

            {getFilteredFeedbacks().length === 0 ? (
                <div className="no-feedbacks">
                    <p>
                        {feedbackIdSet
                            ? feedbacks.length > 0
                                ? `Os IDs buscados (${[...feedbackIdSet].join(', ')}) não foram encontrados nos ${feedbacks.length} feedbacks deste vendedor.`
                                : `Nenhum feedback encontrado para este vendedor/loja. Verifique se o vendedor "${salesman}" e a loja "${storeName}" estão corretos.`
                            : filterType === 'pendentes'
                                ? 'Nenhum feedback pendente de resposta encontrado.'
                                : 'Nenhum feedback encontrado para este vendedor nos últimos 60 dias.'}
                    </p>
                    {feedbackIdSet && feedbacks.length > 0 && (
                        <button
                            className="filter-clear-button"
                            style={{ marginTop: '12px' }}
                            onClick={() => navigate(`/feedbacks?salesman=${encodeURIComponent(salesman)}&store=${encodeURIComponent(storeName)}${fullname ? `&fullname=${encodeURIComponent(fullname)}` : ''}`)}
                        >
                            Ver todos os feedbacks deste vendedor
                        </button>
                    )}
                </div>
            ) : (
                <div className="feedbacks-table-container">
                    <table className="feedbacks-table">
                        <thead>
                            <tr>
                                <th>ID (feedback_id)</th>
                                <th>Data/Hora</th>
                                <th>Duração do Áudio</th>
                                <th>Duração da Transcrição</th>
                                <th>Nota</th>
                                <th>Classificação</th>
                                <th>Feedback</th>
                                <th>Lido</th>
                                <th>Likes</th>
                                <th>Comentários</th>
                                <th>Áudios</th>
                            </tr>
                        </thead>
                        <tbody>
                            {getFilteredFeedbacks().map((feedback, index) => {
                                const isHighlighted = feedbackIdSet?.has(String(feedback.id));
                                return (
                                    <tr key={feedback.id} ref={isHighlighted && index === 0 ? highlightedRowRef : null} className={`feedback-row${isHighlighted ? ' feedback-row-highlighted' : ''}`}>
                                        <td className="feedback-id">{feedback.id}</td>
                                        <td className="feedback-date">{formatDate(feedback.start)}</td>
                                        <td className="feedback-duration" title="Duração real do áudio gravado">{formatAudioLength(feedback.audio_duration)}</td>
                                        <td className="feedback-duration" title="Até o fim da última fala transcrita">{formatAudioLength(feedback.transcription_length)}</td>
                                        <td className="feedback-rating">{feedback.nota_geral || '-'}</td>
                                        <td className="feedback-classification">{feedback.classification_name || '—'}</td>
                                        <td className="feedback-text">
                                            <div className="feedback-content">
                                                {renderFeedbackText(feedback.feedback)}
                                            </div>
                                            {userRole === 'admin' && (
                                                <button
                                                    className="transcription-button"
                                                    onClick={() => handleOpenTranscription(feedback.id, feedback.bucket_name)}
                                                    title="Ver transcrição"
                                                >
                                                    📝 Transcrição
                                                </button>
                                            )}
                                            {userRole === 'admin' && !restrictedAdmin && (
                                                <button
                                                    className="transcription-button"
                                                    style={{ marginLeft: '8px' }}
                                                    onClick={() => handleListenAudio(feedback.id, feedback.bucket_name, false)}
                                                    title="Verificar áudio original"
                                                    disabled={audioPlayer.loading && audioPlayer.feedbackId === feedback.id && !audioPlayer.redacted}
                                                >
                                                    {audioPlayer.loading && audioPlayer.feedbackId === feedback.id && !audioPlayer.redacted
                                                        ? '⏳ Carregando...'
                                                        : audioPlayer.feedbackId === feedback.id && !audioPlayer.redacted && audioPlayer.url
                                                            ? '⏹️ Parar Áudio'
                                                            : '🔊 Verificar Áudio'}
                                                </button>
                                            )}
                                            {userRole === 'admin' && (
                                                <button
                                                    className="transcription-button"
                                                    style={{ marginLeft: '8px' }}
                                                    onClick={() => handleListenAudio(feedback.id, feedback.bucket_name, true)}
                                                    title="Ouvir áudio com os trechos fora do atendimento silenciados"
                                                    disabled={audioPlayer.loading && audioPlayer.feedbackId === feedback.id && audioPlayer.redacted}
                                                >
                                                    {audioPlayer.loading && audioPlayer.feedbackId === feedback.id && audioPlayer.redacted
                                                        ? '⏳ Carregando...'
                                                        : audioPlayer.feedbackId === feedback.id && audioPlayer.redacted && audioPlayer.url
                                                            ? '⏹️ Parar Áudio Anonimizado'
                                                            : '🔇 Ouvir Áudio Anonimizado'}
                                                </button>
                                            )}
                                            {audioPlayer.feedbackId === feedback.id && audioPlayer.url && (
                                                <div className="audio-player-inline">
                                                    <audio
                                                        ref={feedbackAudioRef}
                                                        controls
                                                        src={audioPlayer.url}
                                                        style={{ width: '100%', marginTop: '6px' }}
                                                        onEnded={() => setAudioPlayer({ feedbackId: null, url: null, error: null, loading: false })}
                                                    />
                                                </div>
                                            )}
                                            {audioPlayer.feedbackId === feedback.id && audioPlayer.error && (
                                                <div className="audio-error-message" style={{ color: '#dc3545', fontSize: '12px', marginTop: '6px', padding: '6px 8px', background: '#fff5f5', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
                                                    {audioPlayer.error}
                                                </div>
                                            )}
                                        </td>
                                        <td className="feedback-read">{getReadIcon(feedback.read)}</td>
                                        <td className="feedback-likes">{getLikesIcon(feedback.likes)}</td>
                                        <td className="feedback-comments">
                                            <div className="comments-section">
                                                <div className="feedback-content">
                                                    {feedback.comments || '-'}
                                                </div>
                                                <div className="response-section">
                                                    <textarea
                                                        className="response-textarea"
                                                        placeholder="Digite sua resposta..."
                                                        value={responses[feedback.id] || ''}
                                                        onChange={(e) => handleResponseChange(feedback.id, e.target.value)}
                                                        rows={2}
                                                    />
                                                    <button
                                                        className="save-response-button"
                                                        onClick={() => handleSaveResponse(feedback)}
                                                        disabled={!responses[feedback.id]?.trim() || savingResponses[feedback.id]}
                                                    >
                                                        {savingResponses[feedback.id] ? 'Salvando...' : 'Salvar Resposta'}
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="feedback-audio-comments">
                                            {renderAudioComments(
                                                feedback.audio_comment_ids,
                                                formatDate(feedback.start).split(' ')[0], // Só a data
                                                feedback.bucket_name
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de Transcrição */}
            {transcriptionModal.open && (
                <div className="transcription-modal-overlay" onClick={handleCloseTranscription}>
                    <div className="transcription-modal" onClick={e => e.stopPropagation()}>
                        <div className="transcription-modal-header">
                            <h2>Transcrição - Feedback #{transcriptionModal.feedbackId}</h2>
                            <button className="transcription-modal-close" onClick={handleCloseTranscription}>
                                ✕
                            </button>
                        </div>
                        <div className="transcription-modal-body">
                            {loadingTranscription ? (
                                <div className="transcription-loading">Carregando transcrição...</div>
                            ) : transcriptionData?.error ? (
                                <div className="transcription-error">{transcriptionData.error}</div>
                            ) : transcriptionData ? (
                                <>
                                    {transcriptionData.restricted_admin && (
                                        <div className="transcription-note" style={{ marginBottom: '12px', opacity: 0.8 }}>
                                            Seu acesso mostra apenas a transcrição com contexto anonimizado.
                                        </div>
                                    )}
                                    {!transcriptionData.restricted_admin && (
                                    <>
                                    <div className="transcription-field">
                                        <div className="transcription-field-header">
                                            <label>Transcrição Pura</label>
                                            <button
                                                className={`transcription-copy-button ${copiedField === 'pure' ? 'copied' : ''}`}
                                                onClick={() => handleCopyText(transcriptionData.audio_transcription || '', 'pure')}
                                            >
                                                {copiedField === 'pure' ? '✅ Copiado' : '📋 Copiar'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="transcription-textarea"
                                            readOnly
                                            value={transcriptionData.audio_transcription || 'Sem transcrição disponível'}
                                        />
                                    </div>
                                    <div className="transcription-field">
                                        <div className="transcription-field-header">
                                            <label>Transcrição Anonimizada</label>
                                            <button
                                                className={`transcription-copy-button ${copiedField === 'anon' ? 'copied' : ''}`}
                                                onClick={() => handleCopyText(transcriptionData.anon_transcript || '', 'anon')}
                                            >
                                                {copiedField === 'anon' ? '✅ Copiado' : '📋 Copiar'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="transcription-textarea"
                                            readOnly
                                            value={transcriptionData.anon_transcript || 'Sem transcrição disponível'}
                                        />
                                    </div>
                                    </>
                                    )}
                                    <div className="transcription-field">
                                        <div className="transcription-field-header">
                                            <label>
                                                Transcrição com Contexto Anonimizado
                                                {typeof transcriptionData.anon_context_spans?.length === 'number' && (
                                                    <span style={{ marginLeft: '8px', fontWeight: 'normal', opacity: 0.75 }}>
                                                        ({transcriptionData.anon_context_spans.length} trecho(s) removido(s))
                                                    </span>
                                                )}
                                            </label>
                                            <button
                                                className={`transcription-copy-button ${copiedField === 'ctx' ? 'copied' : ''}`}
                                                onClick={() => handleCopyText(transcriptionData.anon_context_transcript || '', 'ctx')}
                                            >
                                                {copiedField === 'ctx' ? '✅ Copiado' : '📋 Copiar'}
                                            </button>
                                        </div>
                                        <textarea
                                            className="transcription-textarea"
                                            readOnly
                                            value={transcriptionData.anon_context_transcript
                                                || 'Ainda não processada para este atendimento'}
                                        />
                                        {transcriptionData.anon_context_spans?.length > 0 && (
                                            <details style={{ marginTop: '8px' }} open>
                                                <summary style={{ cursor: 'pointer' }}>
                                                    O que foi removido, e por quê
                                                </summary>
                                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                                                    {transcriptionData.anon_context_spans.map((sp) => (
                                                        <li key={sp.i} style={{ marginBottom: '4px' }}>
                                                            <code>{sp.ts}</code> <em>{sp.reason}</em>
                                                            {sp.text ? <> — {sp.text}</> : null}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </details>
                                        )}
                                    </div>
                                </>
                            ) : null}
                        </div>
                        <div className="transcription-modal-footer">
                            <button className="transcription-modal-close-btn" onClick={handleCloseTranscription}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeedbacksAdmin;